import React, { useContext } from "react";
import { useTranslation } from "utilities/i18n";
import {
  useDeletePoliciesForSitesMutation,
  useDeleteSitesMutation
} from "generated/graphql";
import { useModalDispatch, DeleteModal } from "collections/Modal";
import { DISMISS, useNotification } from "collections/Notifications";
import { protect } from "TsRoleGuards";
import { SelectedTenantContext } from "polyrouter/SelectedTenantProvider";

const DeleteSitesModal: React.FC<{
  onSuccess: () => void;
  sites: Array<{ name: string; id: string }>;
}> = ({ sites, onSuccess }) => {
  const { t } = useTranslation();
  const [, deleteSites] = useDeleteSitesMutation();
  const [, deletePoliciesForSites] = useDeletePoliciesForSitesMutation();
  const [addNotification] = useNotification();
  const { tenant } = useContext(SelectedTenantContext);
  const tenantId = tenant?.id ?? "";
  const returnTo = useModalDispatch();

  const removeSites = async () => {
    let success = true;
    const resp = await deleteSites({
      tenantId: tenantId || "",
      ids: sites.map(site => site.id),
      deleteRooms: true
    });

    let deletedSites: string[] = [];
    resp.data?.deleteSites?.forEach(siteResp => {
      if (!siteResp.success || siteResp.error) {
        success = false;
        addNotification({
          type: "error",
          dismiss: DISMISS.DEFAULT,
          message: t("ERROR_REMOVING_SITE", {
            name: sites.find(site => siteResp.id === site.id)!.name,
            message: siteResp.error
          })
        });
      } else if (siteResp.success) {
        deletedSites.push(siteResp.id);
      }
    });

    if (deletedSites.length) {
      // for successfully deleted sites, also delete their policies
      const { error } = await deletePoliciesForSites({
        siteIds: deletedSites,
        tenantId: tenantId
      });
      if (error) {
        addNotification({
          type: "error",
          dismiss: DISMISS.DEFAULT,
          message: t("ERROR_REMOVING_SITE_POLICIES", {
            error: error.message
          })
        });
      }
    }

    if (success && resp.data?.deleteSites) {
      addNotification({
        type: "success",
        dismiss: DISMISS.DEFAULT,
        message: t("SUCCESSFULLY_REMOVED_SITES"),
        persistThroughNav: true
      });
    }
    returnTo();
    onSuccess();
  };

  return (
    <DeleteModal
      name="DeleteSites"
      title={t("REMOVE_SITE", { count: sites.length })}
      confirmationString={t("CONFIRM_REMOVE_SITES", { count: sites.length })}
      action={"REMOVE"}
      onDelete={removeSites}
    >
      <div className="small-text overflow-list modal-body pt-1 pl-1">
        {sites.map((site, i) => (
          <p key={i} className="list-item">
            {site.name}
          </p>
        ))}
      </div>
    </DeleteModal>
  );
};

export default protect("UPDATE_SITES")(DeleteSitesModal);
